import { getIsTemplatesPageEnabled } from '@infield/pages/utils';
import { useContext } from 'react';

import { useIsSchedulerEnabledContext } from './use-is-scheduler-enabled.context';

/**
 * The checklist scheduler will be rolled out site by site. It is also only enabled for checklist admins and if the templates page is enabled.
 * This hook checks if the scheduler is enabled for the current user, project and location.
 *
 * @param flagKey The Unleash feature flag key for the scheduler feature.
 * @returns Boolean indicating whether the scheduler feature is enabled or not.
 */
export const useIsSchedulerEnabled = (flagKey: string): boolean => {
  const {
    useSelectedRootLocationConfiguration,
    useIsChecklistAdminQuery,
    useFeatureToggleConfig,
    useIsLocationEnabledForFeatureFlag,
  } = useIsSchedulerEnabledContext();

  const { data: isChecklistAdmin } = useIsChecklistAdminQuery();

  const selectedRootLocation = useSelectedRootLocationConfiguration();
  const { config: rootLocationFeatureToggles } = useFeatureToggleConfig(
    selectedRootLocation?.externalId ||
      selectedRootLocation?.assetExternalId ||
      '' // This is a workaround because the hook is not yet typed to accept undefined values
  );
  const isTemplatesPageEnabled = getIsTemplatesPageEnabled(
    rootLocationFeatureToggles
  );

  const isCurrentLocationEnabledForScheduler =
    useIsLocationEnabledForFeatureFlag(flagKey, selectedRootLocation);

  const isSchedulerEnabled =
    Boolean(isChecklistAdmin) &&
    isTemplatesPageEnabled &&
    isCurrentLocationEnabledForScheduler;

  return isSchedulerEnabled;
};
